import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon, Platform } from '../components/Icons.jsx';
import { api } from '../services/api/client.js';
import { formatAppDate } from '../utils/locale.js';
import { friendlyErrorMessage, notify } from '../lib/notifications.js';

const WEEK_START = 1;
const VIEW_MODES = ['month', 'week', 'day', 'agenda'];
const STATUS_FILTERS = [
  ['all', 'All'],
  ['planned', 'Needs approval'],
  ['approved', 'Approved'],
  ['publishing', 'Publishing'],
  ['published', 'Published'],
  ['failed', 'Failed'],
  ['partial_failure', 'Partial failure'],
];
const STATUS_LABELS = {
  draft: 'Draft',
  generating: 'Generating',
  ready: 'Ready',
  planned: 'Needs approval',
  approved: 'Approved',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  queued: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  partial_failure: 'Partial failure',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};
const STATUS_SHORT_LABELS = {
  draft: 'Draft',
  generating: 'Gen',
  ready: 'Ready',
  planned: 'Review',
  approved: 'Ready',
  scheduled: 'Sched',
  publishing: 'Posting',
  queued: 'Posting',
  published: 'Posted',
  failed: 'Fail',
  partial_failure: 'Partial',
  rejected: 'Reject',
  cancelled: 'Cancel',
};
const STATUS_PILL_LABELS = {
  draft: 'DRAFT',
  generating: 'GEN',
  ready: 'READY',
  planned: 'REVIEW',
  approved: 'READY',
  scheduled: 'SCHED',
  publishing: 'POSTING',
  queued: 'POSTING',
  published: 'POSTED',
  failed: 'FAILED',
  partial_failure: 'PARTIAL',
  rejected: 'REJECTED',
  cancelled: 'CANCELLED',
};

export const Scheduler = ({ navigate, openPost }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const mobileDefault = typeof window !== 'undefined' && window.innerWidth < 768;
  const view = normalizeView(searchParams.get('view'), mobileDefault ? 'agenda' : 'month');
  const focusPostId = searchParams.get('focus') || null;
  const selectedDate = parseDateParam(searchParams.get('date')) || startOfDay(new Date());
  const filter = normalizeFilter(searchParams.get('status'));
  const visibleRange = useMemo(() => rangeForView(view, selectedDate), [view, selectedDate.getTime()]);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(false);

  useEffect(() => {
    function handleKey(event) {
      if (event.key === 'Escape' && inspectorOpen && !selectedEvent) setInspectorOpen(false);
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [inspectorOpen, selectedEvent]);

  const visibleEvents = useMemo(() => {
    const rangeFiltered = events.filter(event => event.localDateKey >= dateKey(visibleRange.start) && event.localDateKey <= dateKey(visibleRange.end));
    return filter === 'all' ? rangeFiltered : rangeFiltered.filter(event => statusFilterMatch(event.status, filter));
  }, [events, filter, visibleRange.start.getTime(), visibleRange.end.getTime()]);
  const eventsByDate = useMemo(() => groupEventsByDate(visibleEvents), [visibleEvents]);
  const counts = useMemo(() => countStatuses(events), [events]);

  useEffect(() => {
    let cancelled = false;
    async function loadSchedule() {
      try {
        setLoading(true);
        setError(null);
        const fetchRange = calendarFetchRange(selectedDate);
        const response = await api.scheduler.getCalendar(fetchRange.from.toISOString(), fetchRange.to.toISOString(), { status: filter, timezone });
        if (!cancelled) setEvents((response.posts || []).map(row => mapSchedulerEvent(row, timezone)).sort(compareEvents));
      } catch (err) {
        if (!cancelled) setError(friendlyErrorMessage(err, 'Could not load scheduler data.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadSchedule();
    return () => { cancelled = true; };
  }, [selectedDate.getFullYear(), selectedDate.getMonth(), timezone]);

  useEffect(() => {
    if (!focusPostId || loading) return;
    const event = events.find(item => item.id === focusPostId);
    if (!event) return;
    const eventDate = new Date(event.scheduledAt || event.publishedAt || Date.now());
    if (!isSameMonth(eventDate, selectedDate)) {
      updateSchedulerParams({ date: eventDate });
      return;
    }
    setSelectedEvent(event);
    const params = new URLSearchParams(searchParams);
    params.delete('focus');
    setSearchParams(params, { replace: true });
  }, [focusPostId, events, loading]);

  function updateSchedulerParams(next) {
    const params = new URLSearchParams(searchParams);
    if (next.view) params.set('view', next.view);
    if (next.date) params.set('date', dateKey(next.date));
    if (next.status) params.set('status', next.status);
    setSearchParams(params, { replace: true });
  }

  function movePeriod(delta) {
    const next = view === 'month' || view === 'agenda' ? addMonths(selectedDate, delta) : view === 'week' ? addDays(selectedDate, delta * 7) : addDays(selectedDate, delta);
    updateSchedulerParams({ date: next });
  }

  function openCreateForDate(day) {
    sessionStorage.setItem('scheduler_create_prefill', JSON.stringify({ scheduledAt: defaultScheduledAt(day, timezone), timezone }));
    navigate('create');
  }

  function openEvent(event) {
    if (event.rawContent && openPost) openPost(event.rawContent);
    else setSelectedEvent(event);
  }

  async function refreshEvent(id, updater, successKey) {
    setWorkingId(id);
    setActionError(null);
    try {
      const result = await updater();
      if (result) {
        const mapped = mapSchedulerEvent(result, timezone);
        setEvents(previous => previous.map(item => item.id === id ? mapped : item).sort(compareEvents));
        setSelectedEvent(mapped);
      }
      if (successKey) notify(successKey);
    } catch (err) {
      setActionError(friendlyErrorMessage(err, 'Scheduler action failed. Try again.'));
    } finally {
      setWorkingId(null);
    }
  }

  async function rescheduleEvent(event, scheduledAt) {
    await refreshEvent(event.id, () => api.scheduler.reschedule(event.id, { scheduledAt, timezone }), 'scheduler.rescheduled');
  }

  async function approveEvent(event) {
    await refreshEvent(event.id, () => api.scheduler.approve(event.id), 'scheduler.approved');
  }

  async function unscheduleEvent(event) {
    setWorkingId(event.id);
    setActionError(null);
    try {
      await api.scheduler.unschedule(event.id);
      setEvents(previous => previous.filter(item => item.id !== event.id));
      setSelectedEvent(null);
      notify('scheduler.unscheduled');
    } catch (err) {
      setActionError(friendlyErrorMessage(err, 'Post was not unscheduled. Try again.'));
    } finally {
      setWorkingId(null);
    }
  }

  async function retryEvent(event, failedOnly = false) {
    await refreshEvent(event.id, () => api.scheduler.retry(event.id, { failedOnly }), 'scheduler.retryStarted');
  }

  async function deleteEvent(event) {
    setWorkingId(event.id);
    setActionError(null);
    try {
      await api.scheduler.delete(event.id);
      setEvents(previous => previous.filter(item => item.id !== event.id));
      setSelectedEvent(null);
      notify('scheduler.deleted');
    } catch (err) {
      setActionError(friendlyErrorMessage(err, 'Post was not deleted. Try again.'));
    } finally {
      setWorkingId(null);
    }
  }

  async function cloneEvent(event, payload) {
    setWorkingId(event.id);
    setActionError(null);
    try {
      const result = await api.scheduler.clone(event.id, payload);
      const mapped = mapSchedulerEvent(result, timezone);
      setEvents(previous => [...previous, mapped].sort(compareEvents));
      setSelectedEvent(null);
      notify('scheduler.cloned');
    } catch (err) {
      setActionError(friendlyErrorMessage(err, 'Clone was not created. Try again.'));
    } finally {
      setWorkingId(null);
    }
  }

  const rangeTitle = titleForView(view, selectedDate);
  const dayHeaders = weekDays(selectedDate);
  const monthDays = buildMonthDays(selectedDate);
  const agendaDays = buildAgendaDays(view, selectedDate, visibleRange);

  return (
    <div className={`page col scheduler-page-v2 scheduler-view-${view}`} style={{gap:16}}>
      <div className="scheduler-controls">
        <div className="dt-tabs scheduler-status-tabs">
          {STATUS_FILTERS.map(([key, label]) => (
            <button key={key} className={'dt-tab ' + (filter === key ? 'is-active' : '')} onClick={() => updateSchedulerParams({ status: key })}>
              {label} <span className="filter-count">{counts[key] || 0}</span>
            </button>
          ))}
        </div>
        <div className="scheduler-nav-row">
          <div className="seg scheduler-view-switcher">
            {VIEW_MODES.map(mode => <button key={mode} className={view === mode ? 'on' : ''} onClick={() => updateSchedulerParams({ view: mode })}>{titleCase(mode)}</button>)}
          </div>
          <div className="scheduler-period-nav">
            <button className="btn btn-icon" onClick={() => movePeriod(-1)} aria-label="Previous period"><Icon name="chev_l" size={14}/></button>
            <div className="scheduler-period-title"><strong>{rangeTitle}</strong><span>{visibleEvents.length} posts</span></div>
            <button className="btn btn-icon" onClick={() => movePeriod(1)} aria-label="Next period"><Icon name="chev_r" size={14}/></button>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => updateSchedulerParams({ date: new Date() })}>Today</button>
        </div>
      </div>

      {error && <div className="auth-error scheduler-state"><Icon name="alert" size={14} /> <span>Couldn’t load scheduler data.</span><button className="btn btn-secondary btn-sm" onClick={() => updateSchedulerParams({ date: selectedDate })}>Retry</button></div>}
      {loading && <div className="card scheduler-state"><Icon name="refresh" size={12}/> Loading scheduler...</div>}

      {!loading && !error && view === 'month' && (
        <MonthView
          days={monthDays}
          eventsByDate={eventsByDate}
          selectedDate={selectedDate}
          onSelectDay={date => { updateSchedulerParams({ date }); setInspectorOpen(true); }}
          onOpen={openEvent}
          onActions={setSelectedEvent}
          onCreate={openCreateForDate}
          highlightedId={selectedEvent?.id || null}
          inspectorOpen={inspectorOpen}
          onInspectorClose={() => setInspectorOpen(false)}
        />
      )}
      {!loading && !error && view === 'week' && (
        <WeekView days={dayHeaders} eventsByDate={eventsByDate} selectedDate={selectedDate} onSelect={date => updateSchedulerParams({ date })} onOpen={openEvent} onActions={setSelectedEvent} onCreate={openCreateForDate} highlightedId={selectedEvent?.id || null} />
      )}
      {!loading && !error && view === 'day' && (
        <DayList day={selectedDate} events={eventsByDate[dateKey(selectedDate)] || []} onOpen={openEvent} onActions={setSelectedEvent} onCreate={openCreateForDate} highlightedId={selectedEvent?.id || null} />
      )}
      {!loading && !error && view === 'agenda' && (
        <AgendaView days={agendaDays} eventsByDate={eventsByDate} onOpen={openEvent} onActions={setSelectedEvent} onCreate={openCreateForDate} highlightedId={selectedEvent?.id || null} />
      )}
      {!loading && !error && visibleEvents.length === 0 && (
        <div className="card scheduler-empty-state">
          <Icon name="calendar" size={22}/>
          <strong>{filter === 'all' ? `No posts scheduled for ${formatMonthYear(selectedDate)}.` : `No ${STATUS_LABELS[filter] || filter} posts in ${formatMonthYear(selectedDate)}.`}</strong>
          <span>{filter === 'all' ? 'Create your first scheduled post.' : 'Clear the filter or pick another period.'}</span>
          <div className="row" style={{gap:8}}>
            {filter !== 'all' && <button className="btn btn-secondary" onClick={() => updateSchedulerParams({ status: 'all' })}>Clear filter</button>}
            <button className="btn btn-primary" onClick={() => openCreateForDate(selectedDate)}><Icon name="plus" size={14}/> Create post</button>
          </div>
        </div>
      )}

      {selectedEvent && (
        <ScheduledPostModal
          event={selectedEvent}
          working={workingId === selectedEvent.id}
          error={actionError}
          timezone={timezone}
          onClose={() => { setSelectedEvent(null); setActionError(null); }}
          onPreview={() => openEvent(selectedEvent)}
          onApprove={() => approveEvent(selectedEvent)}
          onReschedule={scheduledAt => rescheduleEvent(selectedEvent, scheduledAt)}
          onUnschedule={() => unscheduleEvent(selectedEvent)}
          onRetry={failedOnly => retryEvent(selectedEvent, failedOnly)}
          onDelete={() => deleteEvent(selectedEvent)}
          onClone={payload => cloneEvent(selectedEvent, payload)}
        />
      )}

    </div>
  );
};

function MonthView({ days, eventsByDate, selectedDate, onSelectDay, onOpen, onActions, onCreate, highlightedId, inspectorOpen, onInspectorClose }) {
  const selectedDay = days.find(day => isSameDay(day, selectedDate)) || selectedDate;
  const selectedEvents = eventsByDate[dateKey(selectedDay)] || [];
  return (
    <div className="scheduler-month-workspace">
      <div className="scheduler-month-layout">
        <div className="scheduler-month-frame">
          <div className="card scheduler-card scheduler-month-card" style={{ padding: 0 }}>
            <div className="scheduler-month-board">
              <CalendarHeader />
              <div className="cal-grid">
                {days.map(day => (
                  <CalendarCell
                    key={dateKey(day)}
                    day={day}
                    muted={!isSameMonth(day, selectedDate)}
                    selected={isSameDay(day, selectedDay)}
                    events={eventsByDate[dateKey(day)] || []}
                    onSelect={onSelectDay}
                    onCreate={onCreate}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <MonthDayInspector
          day={selectedDay}
          events={selectedEvents}
          onOpen={onOpen}
          onActions={onActions}
          onCreate={onCreate}
          isOpen={inspectorOpen}
          onClose={onInspectorClose}
        />
      </div>
    </div>
  );
}

function MonthDayInspector({ day, events, onOpen, onActions, onCreate, isOpen, onClose }) {
  const statusCounts = dayStatusCounts(events);
  return (
    <>
      {isOpen && <div className="inspector-scrim" onClick={onClose} />}
      <aside className={'month-day-inspector' + (isOpen ? ' is-open' : '')} aria-label={`Posts for ${formatLongDate(day)}`}>
        <button className="inspector-close" onClick={onClose} aria-label="Close inspector"><Icon name="x" size={16} /></button>
        <div className="month-day-inspector-head">
          <div>
            <div className="eyebrow">Selected day</div>
            <h3>{formatLongDate(day)}</h3>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => onCreate(day)}><Icon name="plus" size={13} /> New</button>
        </div>
        <div className="month-day-inspector-counts">
          <div className="month-day-count-card"><strong>{events.length}</strong><span>{events.length === 1 ? 'post' : 'posts'}</span></div>
          <div className="month-day-status-summary">
            {statusCounts.length ? statusCounts.map(([status, count]) => (
              <span key={status}><StatusBadge status={status} compact /> <strong>{count}</strong></span>
            )) : <span className="caption">No scheduled posts yet.</span>}
          </div>
        </div>
        <div className="month-inspector-posts">
          {events.length ? events.map(event => (
            <MonthInspectorPost key={event.id} event={event} onOpen={onOpen} onActions={onActions} />
          )) : (
            <div className="month-day-empty-panel">
              <Icon name="calendar" size={18} />
              <span>This day is open. Create a post or choose another highlighted day.</span>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function MonthInspectorPost({ event, onOpen, onActions }) {
  function open() { onOpen(event); }
  return (
    <div
      className={'month-inspector-post ' + event.status}
      role="button"
      tabIndex={0}
      style={{ '--platform-accent': platformColor(event.platform) }}
      onClick={open}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
    >
      <div className="month-inspector-post-top">
        <span className="month-inspector-time">{event.time}</span>
        <span className="month-inspector-platforms">{event.platforms.map(platform => <Platform key={platform} name={platform} size={15} />)}</span>
        <StatusBadge status={event.status} />
        <button className="event-actions-btn" onClick={e => { e.stopPropagation(); onActions(event); }} aria-label="Post actions"><Icon name="more" size={13} /></button>
      </div>
      <div className="month-inspector-title">{event.title}</div>
      <div className="month-inspector-meta">{event.targetSummary}</div>
    </div>
  );
}

function WeekView({ days, eventsByDate, selectedDate, onSelect, onOpen, onActions, onCreate, highlightedId }) {
  return <div className="card scheduler-card scheduler-week-card" style={{padding:0}}><div className="week-day-strip">{days.map(day => <button key={dateKey(day)} className={isSameDay(day, selectedDate) ? 'is-active' : ''} onClick={() => onSelect(day)}><span>{formatWeekday(day)}</span><strong>{day.getDate()}</strong></button>)}</div><div className="cal-grid cal-grid-week">{days.map(day => <CalendarCell key={dateKey(day)} day={day} selected={isSameDay(day, selectedDate)} events={eventsByDate[dateKey(day)] || []} onSelect={onSelect} onOpen={onOpen} onActions={onActions} onCreate={onCreate} highlightedId={highlightedId} />)}</div></div>;
}

function AgendaView({ days, eventsByDate, onOpen, onActions, onCreate, highlightedId }) {
  return <div className="scheduler-agenda">{days.map(day => <DayList key={dateKey(day)} day={day} events={eventsByDate[dateKey(day)] || []} onOpen={onOpen} onActions={onActions} onCreate={onCreate} highlightedId={highlightedId} compact />)}</div>;
}

function DayList({ day, events, onOpen, onActions, onCreate, compact, highlightedId }) {
  return <div className="agenda-day"><div className="agenda-day-header">{formatLongDate(day)}</div>{events.length ? events.map(event => <EventRow key={event.id} event={event} onOpen={onOpen} onActions={onActions} isHighlighted={event.id === highlightedId} />) : <div className="scheduler-day-empty">No posts scheduled <button className="btn btn-ghost btn-sm" onClick={() => onCreate(day)}><Icon name="plus" size={13}/> Create post</button></div>}{!compact && <button className="btn btn-secondary scheduler-create-day" onClick={() => onCreate(day)}><Icon name="plus" size={14}/> Create post for this day</button>}</div>;
}

function CalendarHeader() {
  const labels = ['MON','TUE','WED','THU','FRI','SAT','SUN'];
  return <div className="cal-head">{labels.map((day, idx) => <div key={day} className={'cal-head-cell' + (idx >= 5 ? ' is-weekend' : '')}>{day}</div>)}</div>;
}

function CalendarCell({ day, muted, selected, events, onSelect, onCreate }) {
  const dayOfWeek = day.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const statusBars = dayStatusBars(events);
  const statusSummary = dayStatusSummary(events);
  const next = events[0] || null;
  const nextStatusLabel = next ? (STATUS_PILL_LABELS[next.status] || (next.status || '').toUpperCase()) : '';

  function selectDay() { if (onSelect) onSelect(day); }

  const classes = [
    'cal-cell',
    muted && 'muted',
    selected && 'is-selected',
    isToday(day) && 'is-today',
    isWeekend && 'is-weekend',
    events.length > 0 && 'has-events',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      role="button"
      tabIndex={0}
      onClick={selectDay}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectDay(); } }}
      onDoubleClick={() => onCreate(day)}
      aria-label={`${formatLongDate(day)}${events.length ? `, ${events.length} posts` : ', no posts'}`}
    >
      <div className="cal-cell-head">
        <span className="cal-day-num">{day.getDate()}</span>
        <button className="cal-create-btn" onClick={e => { e.stopPropagation(); onCreate(day); }} aria-label={`Create post for ${formatLongDate(day)}`}>
          <Icon name="plus" size={12} />
        </button>
      </div>
      {events.length > 0 && (
        <div className="cal-day-overview">
          <div className="cal-day-status-stack" aria-hidden="true">
            {statusBars.map(bar => (
              <span key={bar.status} className={bar.status} style={{ '--w': bar.weight }} />
            ))}
          </div>
          <div className="cal-day-brief">
            <span className="cal-day-total">{events.length} {events.length === 1 ? 'post' : 'posts'}</span>
            <span className="cal-day-mix">{statusSummary}</span>
          </div>
          {next && (
            <div className="cal-day-next" data-status={next.status}>
              <div className="cal-day-next-meta">
                <span className="cal-day-next-time">{next.time}</span>
                <span className="cal-day-next-platforms">
                  {next.platforms.slice(0, 3).map(platform => <Platform key={platform} name={platform} size={12} />)}
                </span>
              </div>
              <div className="cal-day-next-status-row"><span className="cal-day-next-status">{nextStatusLabel}</span></div>
              <div className="cal-day-next-title">{next.title}</div>
              {events.length > 1 && <div className="cal-day-more">+{events.length - 1} more</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EventRow({ event, onOpen, onActions, isHighlighted }) {
  return <div className={'agenda-item ' + event.accent + (isHighlighted ? ' is-highlighted' : '')} role="button" tabIndex={0} onClick={() => onOpen(event)} onKeyDown={e => e.key === 'Enter' && onOpen(event)}><div className="agenda-item-time">{event.time}</div><div className="agenda-item-platforms">{event.platforms.map(platform => <Platform key={platform} name={platform} size={14}/>)}</div><div className="agenda-item-title">{event.title}</div><StatusBadge status={event.status} className="agenda-item-status"/><button className="event-actions-btn" onClick={e => { e.stopPropagation(); onActions(event); }} aria-label="Post actions"><Icon name="more" size={13}/></button></div>;
}

function StatusBadge({ status, className = '', compact = false }) {
  return <span className={'cal-status ' + status + ' ' + (compact ? 'is-compact ' : '') + className}>{compact ? STATUS_SHORT_LABELS[status] || STATUS_LABELS[status] || status : STATUS_LABELS[status] || status}</span>;
}

function ScheduledPostModal({ event, working, error, timezone, onClose, onPreview, onApprove, onReschedule, onUnschedule, onRetry, onDelete, onClone }) {
  const [scheduledAt, setScheduledAt] = useState(toDatetimeLocalValue(event.scheduledAt || new Date().toISOString()));
  const [cloneOpen, setCloneOpen] = useState(false);
  const canApprove = event.status === 'planned';
  const canMove = ['planned', 'approved', 'scheduled', 'failed', 'partial_failure'].includes(event.status);
  const canRetry = ['failed', 'partial_failure'].includes(event.status) && event.canRetry;
  const destructiveDisabled = ['publishing', 'queued'].includes(event.status);

  useEffect(() => { setScheduledAt(toDatetimeLocalValue(event.scheduledAt || new Date().toISOString())); setCloneOpen(false); }, [event.id, event.scheduledAt]);

  return <div className="modal-scrim" onClick={onClose}><div className="modal scheduled-post-modal" onClick={e => e.stopPropagation()}><button className="modal-x" onClick={onClose} aria-label="Close scheduled post"><Icon name="x" size={16}/></button><div className="eyebrow">Scheduled post</div><h2 className="h2" style={{marginTop:6}}>{event.title}</h2><div className="row" style={{gap:6, flexWrap:'wrap', marginTop:12}}><StatusBadge status={event.status}/>{event.platforms.map(platform => <Platform key={platform} name={platform} size={18}/>)}<span className="chip">{event.time}</span></div><p className="caption" style={{marginTop:12}}>{event.lifecycleSummary}</p>{error && <div className="auth-error" style={{marginTop:12}}><Icon name="alert" size={14}/> {error}</div>}<div className="scheduler-action-grid"><button className="btn btn-primary" onClick={onPreview}>Open preview</button>{canApprove && <button className="btn btn-secondary" disabled={working} onClick={onApprove}>{working ? 'Approving...' : 'Approve'}</button>}<button className="btn btn-secondary" onClick={() => navigator.clipboard?.writeText(event.contentPreview || event.title)}>Copy text</button><button className="btn btn-secondary" onClick={() => setCloneOpen(value => !value)}>Clone to platforms</button>{canRetry && <button className="btn btn-secondary" disabled={working} onClick={() => onRetry(event.status === 'partial_failure')}>{working ? 'Retrying...' : event.status === 'partial_failure' ? 'Retry failed platforms' : 'Retry publishing'}</button>}</div>{event.errorDetails.length > 0 && <details className="scheduler-secondary-actions"><summary>View errors</summary><div className="scheduler-error-list">{event.errorDetails.map((item, index) => <div key={`${item.platform}-${index}`}><strong>{platformLabel(item.platform)}</strong><span>{item.message || 'Publishing failed'}</span></div>)}</div></details>}{cloneOpen && <ClonePanel event={event} timezone={timezone} working={working} onClone={onClone}/>}<details className="scheduler-secondary-actions" open><summary>Schedule actions</summary><div className="grid-2-eq" style={{marginTop:12}}><input className="input" type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} disabled={!canMove}/><button className="btn btn-secondary" disabled={!canMove || working || !datetimeLocalToIso(scheduledAt)} onClick={() => onReschedule(datetimeLocalToIso(scheduledAt))}>Reschedule</button></div><div className="row" style={{gap:8, marginTop:10, flexWrap:'wrap'}}><button className="btn btn-ghost" disabled={!canMove || working || destructiveDisabled} onClick={onUnschedule}>Unschedule</button><button className="btn btn-ghost" disabled={working || destructiveDisabled} onClick={onDelete}>Delete</button><button className="btn btn-ghost" disabled>Edit</button></div></details></div></div>;
}

function ClonePanel({ event, timezone, working, onClone }) {
  const [platforms, setPlatforms] = useState(event.platforms);
  const [mode, setMode] = useState('same_content');
  const [scheduleMode, setScheduleMode] = useState('draft');
  const [scheduledAt, setScheduledAt] = useState(toDatetimeLocalValue(event.scheduledAt || new Date().toISOString()));
  const platformOptions = ['instagram', 'facebook', 'linkedin', 'telegram', 'x'];
  return <div className="scheduler-clone-panel"><h3>Clone post to platforms</h3><p className="caption">Original: {event.platforms.map(platformLabel).join(', ')} · {event.time}</p><div className="scheduler-platform-options">{platformOptions.map(platform => <label key={platform}><input type="checkbox" checked={platforms.includes(platform)} onChange={() => setPlatforms(value => value.includes(platform) ? value.filter(item => item !== platform) : [...value, platform])}/>{platformLabel(platform)}</label>)}</div><div className="scheduler-radio-row"><label><input type="radio" checked={mode === 'same_content'} onChange={() => setMode('same_content')}/> Use same content</label><label><input type="radio" checked={mode === 'adapt_per_platform'} onChange={() => setMode('adapt_per_platform')} disabled/> Adapt with AI soon</label></div><div className="scheduler-radio-row"><label><input type="radio" checked={scheduleMode === 'draft'} onChange={() => setScheduleMode('draft')}/> Save as draft</label><label><input type="radio" checked={scheduleMode === 'schedule'} onChange={() => setScheduleMode('schedule')}/> Schedule later</label><label><input type="radio" checked={scheduleMode === 'publish_now'} onChange={() => setScheduleMode('publish_now')}/> Publish now</label></div>{scheduleMode === 'schedule' && <input className="input" type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}/>}<button className="btn btn-primary" disabled={!platforms.length || working} onClick={() => onClone({ targetPlatforms: platforms, mode, scheduleMode, scheduledAt: scheduleMode === 'schedule' ? datetimeLocalToIso(scheduledAt) : null, timezone })}>Create clone</button></div>;
}

function mapSchedulerEvent(row, timezone) {
  const post = row.post || row;
  const targets = row.targets || [];
  const content = row.content || null;
  const scheduledAt = post.scheduledAt || post.scheduled_at || targets.find(target => target.scheduledAt)?.scheduledAt;
  const publishedAt = post.publishedAt || post.published_at || targets.find(target => target.publishedAt)?.publishedAt;
  const displayDate = localDateParts(scheduledAt || publishedAt || new Date().toISOString(), timezone);
  const status = normalizeEventStatus(post.status || 'planned', targets);
  const platforms = uniquePlatforms(targets);
  const errorDetails = targets.filter(isFailedTarget).map(target => ({ platform: target.platform, message: target.lastErrorMessage || target.errorMessage || target.last_error_message, code: target.lastErrorCode, retryable: !['needs_reconnect', 'needs_manual_action'].includes(target.status) }));
  const finalPlatforms = platforms.length ? platforms : ['linkedin'];
  return { id: post.id, raw: row, rawContent: content, contentPreview: content?.activeVersion?.body || content?.activeVersion?.caption || content?.contentItem?.title || '', scheduledAt, publishedAt, timezone: post.timezone || timezone, localDateKey: displayDate.key, time: displayDate.time, title: scheduledTitle(post, targets, content), targetSummary: scheduledTargetSummary(targets), lifecycleSummary: lifecycleSummary(post, targets), platforms: finalPlatforms, platform: finalPlatforms[0], status, accent: status === 'failed' || status === 'partial_failure' ? 'red' : status === 'published' ? 'published' : platforms.includes('instagram') ? 'violet' : 'gold', canRetry: errorDetails.some(item => item.retryable), failedPlatforms: errorDetails.map(item => item.platform), publishedPlatforms: targets.filter(target => target.status === 'published').map(target => target.platform), errorDetails };
}

function platformColor(platform) {
  switch (platform) {
    case 'instagram': return '#DD2A7B';
    case 'tiktok': return '#26C9D5';
    case 'linkedin': return '#0A66C2';
    case 'facebook': return '#1877F2';
    case 'telegram': return '#229ED9';
    case 'x':
    case 'twitter': return '#9CA3AF';
    case 'threads': return '#A78BFA';
    default: return '#8B7AFF';
  }
}

function normalizeEventStatus(status, targets) {
  const hasPublished = targets.some(target => target.status === 'published');
  const failed = targets.filter(isFailedTarget);
  if (hasPublished && failed.length) return 'partial_failure';
  if (failed.length) return 'failed';
  if (targets.some(target => ['queued', 'publishing'].includes(target.status))) return 'publishing';
  if (targets.length && targets.every(target => target.status === 'published')) return 'published';
  if (status === 'queued') return 'publishing';
  return status || 'planned';
}

function isFailedTarget(target) { return ['failed', 'needs_reconnect', 'needs_manual_action'].includes(target.status); }
function scheduledTitle(post, targets, content) { return content?.contentItem?.title || content?.activeVersion?.hook || content?.contentItem?.topic || `${targets[0] ? platformLabel(targets[0].platform) : 'Scheduled'} post`; }
function scheduledTargetSummary(targets) { const labels = targets.map(target => target.socialAccount?.displayName || platformLabel(target.platform)); return labels.length <= 3 ? labels.join(', ') || 'No targets' : `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`; }
function lifecycleSummary(post, targets) { if (post.status === 'planned') return 'Needs approval before publishing.'; if (!targets.length) return STATUS_LABELS[post.status] || post.status || 'No targets'; const failed = targets.filter(isFailedTarget); const published = targets.filter(target => target.status === 'published'); if (published.length && failed.length) return `${published.length} published, ${failed.length} failed`; if (failed.length) return failed[0].lastErrorMessage || `${failed.length} failed`; if (published.length) return `${published.length} published`; return targets.map(target => `${platformLabel(target.platform)} ${STATUS_LABELS[target.status] || target.status}`).join(', '); }
function uniquePlatforms(targets) { return Array.from(new Set(targets.map(target => target.platform).filter(Boolean))); }
function compareEvents(a, b) { return String(a.scheduledAt || '').localeCompare(String(b.scheduledAt || '')); }
function groupEventsByDate(items) { return items.reduce((acc, item) => { (acc[item.localDateKey] = acc[item.localDateKey] || []).push(item); return acc; }, {}); }
function countStatuses(items) { const counts = { all: items.length, planned: 0, approved: 0, publishing: 0, published: 0, failed: 0, partial_failure: 0 }; for (const item of items) counts[item.status] = (counts[item.status] || 0) + 1; return counts; }
function dayStatusTone(events) { if (!events.length) return 'empty'; if (events.some(event => ['failed', 'partial_failure'].includes(event.status))) return 'danger'; if (events.some(event => ['publishing', 'queued'].includes(event.status))) return 'live'; if (events.every(event => event.status === 'published')) return 'published'; if (events.some(event => ['approved', 'scheduled'].includes(event.status))) return 'scheduled'; return 'planned'; }
function dayStatusDots(events) { const preferred = ['failed', 'partial_failure', 'publishing', 'published', 'approved', 'scheduled', 'planned']; const statuses = new Set(events.map(event => event.status)); return preferred.filter(status => statuses.has(status)).slice(0, 3); }
function dayStatusBars(events) {
  const order = ['failed', 'partial_failure', 'publishing', 'queued', 'published', 'approved', 'scheduled', 'planned', 'draft'];
  const counts = {};
  for (const event of events) counts[event.status] = (counts[event.status] || 0) + 1;
  return order.filter(status => counts[status]).map(status => ({ status, weight: counts[status] }));
}
function dayStatusSummary(events) { return dayStatusCounts(events).slice(0, 2).map(([status, count]) => `${count} ${String(STATUS_LABELS[status] || status).toLowerCase()}`).join(' · '); }
function dayStatusCounts(events) { const order = ['failed', 'partial_failure', 'publishing', 'published', 'approved', 'scheduled', 'planned', 'draft']; const counts = {}; for (const event of events) counts[event.status] = (counts[event.status] || 0) + 1; const statuses = [...order.filter(status => counts[status]), ...Object.keys(counts).filter(status => !order.includes(status))]; return statuses.map(status => [status, counts[status]]); }
function statusFilterMatch(status, filter) { return filter === 'publishing' ? ['publishing', 'queued'].includes(status) : status === filter; }
function normalizeFilter(value) { return STATUS_FILTERS.some(([key]) => key === value) ? value : 'all'; }
function normalizeView(value, fallback) { return VIEW_MODES.includes(value) ? value : fallback; }
function parseDateParam(value) { if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null; const [year, month, day] = value.split('-').map(Number); return new Date(year, month - 1, day); }
function startOfDay(date) { return new Date(date.getFullYear(), date.getMonth(), date.getDate()); }
function startOfMonth(date) { return new Date(date.getFullYear(), date.getMonth(), 1); }
function endOfMonth(date) { return new Date(date.getFullYear(), date.getMonth() + 1, 0); }
function startOfWeek(date) { const day = date.getDay(); const diff = (day - WEEK_START + 7) % 7; return addDays(startOfDay(date), -diff); }
function endOfWeek(date) { return addDays(startOfWeek(date), 6); }
function addDays(date, days) { const next = new Date(date); next.setDate(next.getDate() + days); return next; }
function addMonths(date, months) { return new Date(date.getFullYear(), date.getMonth() + months, Math.min(date.getDate(), 28)); }
function eachDay(start, end) { const days = []; for (let day = startOfDay(start); day <= end; day = addDays(day, 1)) days.push(day); return days; }
function buildMonthDays(date) { return eachDay(startOfWeek(startOfMonth(date)), endOfWeek(endOfMonth(date))); }
function weekDays(date) { return eachDay(startOfWeek(date), endOfWeek(date)); }
function rangeForView(view, date) { if (view === 'week') return { start: startOfWeek(date), end: endOfWeek(date) }; if (view === 'day') return { start: startOfDay(date), end: startOfDay(date) }; return { start: startOfWeek(startOfMonth(date)), end: endOfWeek(endOfMonth(date)) }; }
function calendarFetchRange(date) { return { from: startOfWeek(startOfMonth(date)), to: addDays(endOfWeek(endOfMonth(date)), 1) }; }
function buildAgendaDays(view, date, range) { return view === 'agenda' ? eachDay(startOfMonth(date), endOfMonth(date)) : eachDay(range.start, range.end); }
function dateKey(date) { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`; }
function isSameDay(a, b) { return dateKey(a) === dateKey(b); }
function isSameMonth(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth(); }
function isToday(date) { return isSameDay(date, new Date()); }
function formatMonthYear(date) { return formatAppDate(date, { month: 'long', year: 'numeric' }); }
function formatLongDate(date) { return formatAppDate(date, { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }); }
function formatWeekday(date) { return formatAppDate(date, { weekday: 'short' }); }
function titleForView(view, date) { if (view === 'week') return `${formatLongDate(startOfWeek(date))} - ${formatLongDate(endOfWeek(date))}`; if (view === 'day') return formatLongDate(date); return formatMonthYear(date); }
function titleCase(value) { return value.slice(0, 1).toUpperCase() + value.slice(1); }
function localDateParts(value, timezone) { const date = new Date(value); const safe = Number.isNaN(date.getTime()) ? new Date() : date; const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(safe).filter(part => part.type !== 'literal').map(part => [part.type, part.value])); return { key: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` }; }
function defaultScheduledAt(day) { const date = new Date(day); date.setHours(9, 0, 0, 0); return date.toISOString(); }
function toDatetimeLocalValue(value) { const date = new Date(value); const safeDate = Number.isNaN(date.getTime()) ? new Date(Date.now() + 15 * 60 * 1000) : date; const local = new Date(safeDate.getTime() - safeDate.getTimezoneOffset() * 60000); return local.toISOString().slice(0, 16); }
function datetimeLocalToIso(value) { const date = new Date(value); return Number.isNaN(date.getTime()) ? null : date.toISOString(); }
function platformLabel(platform) { const labels = { instagram: 'Instagram', threads: 'Threads', telegram: 'Telegram', x: 'X', twitter: 'X', tiktok: 'TikTok', linkedin: 'LinkedIn', facebook: 'Facebook Page' }; return labels[platform] || platform; }
